import React, { useEffect, useState } from "react";
import { Bell, BellOff, Check } from "lucide-react";
import { api } from "../lib/api";

const KEY = "bdd_push_state"; // "subscribed" | "denied" | "dismissed" | "default"

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export default function PushBanner() {
  const [state, setState] = useState(() => {
    if (typeof window === "undefined") return "default";
    return localStorage.getItem(KEY) || "default";
  });
  const [busy, setBusy] = useState(false);

  const supported =
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window;

  const subscribe = async () => {
    if (!supported) return;
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.register("/sw.js");
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        localStorage.setItem(KEY, "denied");
        setState("denied");
        return;
      }
      const { data } = await api.get("/push/public-key");
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
      const subObj = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: subObj.endpoint,
        keys: subObj.keys,
      });
      localStorage.setItem(KEY, "subscribed");
      setState("subscribed");
    } catch (e) {
      console.error("push subscribe failed", e);
      localStorage.setItem(KEY, "denied");
      setState("denied");
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem(KEY, "dismissed");
    setState("dismissed");
  };

  // Hide if not supported or user-dismissed or already subscribed
  if (!supported) return null;
  if (state === "subscribed" || state === "dismissed" || state === "denied")
    return null;

  return (
    <div
      data-testid="push-banner"
      className="border-b border-[#1f2c4a] bg-gradient-to-r from-[#13193a] via-[#1a2647] to-[#13193a]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-[#FBD51C]/15 flex items-center justify-center shrink-0">
            <Bell size={16} className="text-[#FBD51C]" />
          </div>
          <div className="text-sm text-[#b6c1d6] min-w-0">
            <span className="font-bold text-white">Mai più offerte perse</span>{" "}
            <span className="text-[#6e7896]">— Attiva le notifiche e ricevi un alert al volo per ogni offerta HOT</span>
          </div>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <button
            data-testid="push-dismiss"
            onClick={dismiss}
            className="text-xs text-[#6e7896] hover:text-white px-3 py-1.5"
          >
            Non ora
          </button>
          <button
            data-testid="push-subscribe"
            onClick={subscribe}
            disabled={busy}
            className="inline-flex items-center gap-1.5 bg-[#FBD51C] hover:bg-[#FFE552] text-black font-black text-xs uppercase tracking-wider px-3 py-2 rounded-lg brand-cta-glow disabled:opacity-60"
          >
            {busy ? "..." : (
              <>
                <Bell size={12} /> Attiva
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export function PushStatusInline() {
  // small badge to show subscription status anywhere
  const state = typeof window !== "undefined" ? localStorage.getItem(KEY) : null;
  if (state !== "subscribed") return null;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-mono-bdd uppercase tracking-wider text-emerald-400">
      <Check size={11} /> Notifiche attive
    </span>
  );
}
